import { css } from '@emotion/react';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { Box, Button, Dialog, Stack } from '@mui/material';
import {
  ColDef,
  ColumnRowGroupChangedEvent,
  ColumnState,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetRowIdParams,
  GridApi,
  GetMainMenuItems,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  GridState,
  IsGroupOpenByDefaultParams,
  ProcessCellForExportParams,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { clone } from 'lodash-es';
import {
  PropsWithChildren,
  ReactNode,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldPath } from 'react-hook-form';

import { AgTable } from '../../../../../components/AgTable/AgTable';
import { ExportGridButton } from '../../../../../components/AgTable/ExportGridButton';
import { commonProcessCb } from '../../../../../components/AgTable/exportToExcelDefs';
import { GenericFallbacksWrapper } from '../../../../../components/Fallback/GenericFallbacksWrapper';
import { MaxHeaderHeightInRems } from '../../../../../constants/styles';
import { SearchInput } from '../../../../../CoreComponents/base/SearchInput';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { IInvestmentDataModel } from '../../../../../data-models/investment.data-model';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import { IRoundDataModel } from '../../../../../data-models/round.data-model';
import { COMPANY_VIEW_TYPE } from '../../../../../data-models/view-config.data-model';
import { ViewModel } from '../../../../../models/view.model';
import { PermissionService } from '../../../../../services/PermissionService';
import { roundsByIdMapAtom } from '../../../../../services/state/AppConfigStateJ';
import { colors } from '../../../../../theme/colors';
import { AggFuncConfigs } from '../../../../../types';
import { genericComparator } from '../../../../../util/comparators';
import { useCompanyMapLookup } from '../../../../../services/hooks/useSaveCompanyData';
import { SubSectionTitle } from '../../../../CompanyProfiles/Summary/components/SubSection';
import { genericKeyCreator } from '../../../../Finance2/common-grid-defs/commonColDefs';
import { createAuditViewMenuItems } from '../../../hooks/auditViewMenuItems';
import { ColumnSelectorProvider } from '../../../providers/ColumnSelectorProvider';
import {
  filteredMetricsPublishAtom,
  IFilteredMetricsPublish,
  publishedAsOfDateByViewAtom,
} from '../../../state/MetricsStateJ';
import { selectedDateByViewPFJ } from '../../../state/PageStateJ';
import { roundTrackerMetricsFilteredByViewAtom } from '../../../state/RoundTrackerMetricsStateJ';
import { isFirstRenderDonePFJ } from '../../../state/UIStateJ';
import { aggFuncConfigState } from '../../../state/ViewStateJ';
import { customAggFuncs } from '../columns/AggregationConfigs';
import { useColumns } from '../../../hooks/useColumns/useColumns';
import { ColumnsDefinitionProvider, useColumnsDefinition } from '../columns/ColumnDefinitionProvider';
import { getQuickSearchColumnsFromPrefs, getQuickSearchPlaceholderText } from '../columns/portCoColDefs';
import { CompanyNameAndLogo } from '../columns/PredefinedColumns/CompanyNameAndLogo/CompanyNameAndLogo';
import { CompanyRT } from '../columns/PredefinedColumns/CompanyNameAndLogo/CompanyRT';
import { DrillDown } from '../columns/PredefinedColumns/DrillDown/DrillDown';
import { initialGridStateFor, isRowGroupExpandedByDefault } from '../../../state/defaultGridState';
import { ColumnsSettingsDropdown } from '../ColumnsSettingsDropdown/ColumnsSettingsDropdown';
import { CompanyViewGridEventType, ICompanyViewGridEvent } from '../CompanyViewGridEvent';
import { FundFilterComponent } from '../CustomFilterComponents/FundFilterComponent';
import { pfGridApiAtom, quickFilterAtom, quickFilterTextFor, usePfGridState } from '../PfState';
import { RoundsModalContents } from '../RoundModal/RoundsModalContents';
import { IPortfolioTreeRow } from '../../../tree/computeTreeRows';
import { publishedRowsFromTree } from '../../../tree/publishedRowsFromTree';
import { visibleTreeLeafKeys } from '../../../tree/repoolTreeRows';
import { wrapAggFuncForTree } from '../../../tree/treeAggFuncs';
import { companyPassesFundFilter } from './fundFilter';
import { usePortfolioGridFileName } from './usePortfolioGridFileName';

export type SharedTableContext = {
  asOfDate: Date | null;
  totalCompanies: number;
  roundsById: Map<number, IRoundDataModel>;
  quickFilterPrefs: Set<FieldPath<ICompanyDataModel>>;
  aggFuncs?: AggFuncConfigs;
};

const ExcludeFromExport = new Set([DrillDown.NAME]);

// AG Grid's built-in auto group column id (the "Group" column shown when grouping).
const AUTO_GROUP_COL_ID = 'ag-Grid-AutoColumn';

const CustomCompanyColumns = new Set([CompanyNameAndLogo.NAME, CompanyRT.NAME]);

const EMPTY_METRICS_BY_COMPANY_ID: ReadonlyMap<number, IMetricsDataModel[]> = new Map();

/** A fund split gives one company many rows; "Total (N)" and the KPI bar count companies. */
export function distinctCompanyCount(rows: readonly unknown[]): number {
  const companyIds = new Set<number>();
  for (const row of rows) {
    const companyId = (row as { companyId?: number } | null | undefined)?.companyId;
    if (companyId != null) {
      companyIds.add(companyId);
    }
  }
  return companyIds.size;
}

/** Wraps each aggFunc so a node's pooled value wins over its children, and drops the row-grouping
 * options `treeData` cannot support. */
export function treeColumnDefs(columnsDef: ColDef[]): ColDef[] {
  return columnsDef.map(({ allowedAggFuncs: _allowed, enableRowGroup: _group, ...rest }) => {
    if (rest.aggFunc == null) {
      return rest;
    }
    return { ...rest, aggFunc: wrapAggFuncForTree(rest.aggFunc, rest.field) };
  });
}

/** Choosing an aggregation calls `setColumnAggFunc`, which would replace the wrapper and silently
 * overwrite every node's pooled value. */
export function withoutAggregationMenu(base: GetMainMenuItems): GetMainMenuItems {
  return (params) => {
    const items = base(params);
    return Array.isArray(items) ? items.filter((item) => item !== 'valueAggSubMenu') : items;
  };
}

function withoutGroupingSlices(state: GridState): GridState {
  const { rowGroup: _rowGroup, aggregation: _aggregation, rowGroupExpansion: _expansion, ...rest } = state;
  return rest;
}

/** Structure has to lead: a view saved in Flat mode does not list the auto column at all, so its own
 * pinned column would open the grid. Reading only — never persisted. */
export function treeInitialState(state: GridState | undefined): GridState | undefined {
  if (!state) {
    return state;
  }
  const rest = withoutGroupingSlices(state);
  return { ...rest, ...autoColumnFirst(rest) };
}

function firstWithAutoColumn(colIds: string[] | undefined) {
  return [AUTO_GROUP_COL_ID, ...(colIds ?? []).filter((colId) => colId !== AUTO_GROUP_COL_ID)];
}

function autoColumnFirst(state: GridState): Partial<GridState> {
  const slices: Partial<GridState> = {};
  // ag-grid force-shows any column missing from a `columnOrder` it is given, so never invent the slice.
  if (state.columnOrder?.orderedColIds?.length) {
    slices.columnOrder = { orderedColIds: firstWithAutoColumn(state.columnOrder.orderedColIds) };
  }
  // An empty `leftColIds` would tell ag-grid to unpin every column the colDefs pin.
  if (state.columnPinning?.leftColIds?.length) {
    slices.columnPinning = {
      ...state.columnPinning,
      leftColIds: firstWithAutoColumn(state.columnPinning.leftColIds),
    };
  }
  return slices;
}

const without = (colIds: string[] | undefined, colId: string) => (colIds ?? []).filter((id) => id !== colId);

/** ag-grid reports the tree's own pinned auto column in live state; persisting it would pin and lead
 * Flat mode's "Group" column for every reader of the view. */
function withoutTreeAutoColumn(live: GridState, preserved: GridState | undefined): Partial<GridState> {
  const slices: Partial<GridState> = {};
  if (live.columnOrder && !preserved?.columnOrder?.orderedColIds?.includes(AUTO_GROUP_COL_ID)) {
    slices.columnOrder = { orderedColIds: without(live.columnOrder.orderedColIds, AUTO_GROUP_COL_ID) };
  }
  if (live.columnPinning && !preserved?.columnPinning?.leftColIds?.includes(AUTO_GROUP_COL_ID)) {
    slices.columnPinning = {
      ...live.columnPinning,
      leftColIds: without(live.columnPinning.leftColIds, AUTO_GROUP_COL_ID),
    };
  }
  return slices;
}

/** Without this the first sort / filter in tree mode persists a `gridState` with no `rowGroup`,
 * destroying the view's saved grouping for everyone who reads it. */
export function restoreGroupingIntoState(live: GridState, preserved: GridState | undefined): GridState {
  const restored: GridState = { ...withoutGroupingSlices(live), ...withoutTreeAutoColumn(live, preserved) };
  if (preserved?.rowGroup) {
    restored.rowGroup = preserved.rowGroup;
  }
  if (preserved?.aggregation) {
    restored.aggregation = preserved.aggregation;
  }
  if (preserved?.rowGroupExpansion) {
    restored.rowGroupExpansion = preserved.rowGroupExpansion;
  }
  return restored;
}

export const pfBaseGridOptions = {
  // FD-4123: ColumnsDefinitionProvider re-pushes columnsDef into the live grid, and
  // ag-grid drops the view's saved column order on replaced colDefs without this.
  maintainColumnOrder: true,
  cacheQuickFilter: true,
  // Keep the horizontal scrollbar always visible so its appear/disappear doesn't
  // toggle the available container width. With `flex: 1` on every column, that
  // toggle redistributes widths and can loop at column-resize boundaries.
  alwaysShowHorizontalScroll: true,
} satisfies GridOptions;
export const GridPanelContainer = css`
  width: 100%;
  & .ag-theme-1.ag-theme-foresight .ag-root-wrapper {
    border-radius: 0 0 4px 4px;
  }
  & .grid-panel {
    background-color: ${colors.primary[0]};
  }
`;

const PfGridContainer = css`
  width: 100%;
  height: calc(100vh - ${MaxHeaderHeightInRems}rem);
  padding: 0 2rem 2rem;
`;

const PfGridHeadingSection = css`
  width: 100%;
  position: relative;
  padding: 1rem 2rem 0;
`;

interface ISharedTableProps {
  rowData: IMetricsDataModel[] | IInvestmentDataModel[] | undefined;
  /** The fund scoping `rowData` was built under, published with the rows so they cannot tear. */
  fundBasis?: number[] | null;
  /** Plain date `rowData` was built for, published with the rows so a held-over publish reads as stale. */
  asOfDate?: string | null;
  view: ViewModel;
  setRowGroupColumns?: (groups: ColumnState[]) => void;
  loading?: boolean;
  treeData?: boolean;
  getDataPath?: (row: IMetricsDataModel) => string[];
  treeAutoGroupColumnDef?: ColDef;
  /** Published in place of the tree leaves, which are per (company, vehicle). */
  treeMetricsByCompanyId?: ReadonlyMap<number, IMetricsDataModel[]>;
  /** Tree mode: the leaf keys passing the grid's filters, re-emitted on every publish (FD-4988). */
  onVisibleTreeLeavesChange?: (keys: ReadonlySet<string>) => void;
  headingControls?: ReactNode;
}

function SharedTableImpl({
  rowData,
  fundBasis = null,
  asOfDate = null,
  view,
  setRowGroupColumns,
  loading,
  treeData = false,
  getDataPath,
  treeAutoGroupColumnDef,
  treeMetricsByCompanyId,
  onVisibleTreeLeavesChange,
  headingControls,
}: ISharedTableProps) {
  const gridApiRef = useRef<GridApi | null>(null);

  const { columnsDef } = useColumnsDefinition();
  const columnsMeta = useColumns();
  const setGridApi = useSetAtom(pfGridApiAtom);
  const setIsFirstRenderDone = useSetAtom(isFirstRenderDonePFJ);
  const filteredFunds = view.filteredFunds;
  const [companyIdsCount, setCompanyIdsCount] = useState(0);
  const setFilteredRoundTrackerMetrics = useSetAtom(roundTrackerMetricsFilteredByViewAtom(view.id));
  const setFilteredMetricsPublish = useSetAtom(filteredMetricsPublishAtom(view.id));
  const setPublishedAsOfDate = useSetAtom(publishedAsOfDateByViewAtom(view.id));
  // lazy-initialized so the factory runs once per mount, not per render
  const [basisTracker] = useState(() => createPublishedBasisTracker(fundBasis, asOfDate));
  const viewDate = useAtomValue(selectedDateByViewPFJ(view.id));
  const getCompany = useCompanyMapLookup();
  const [showRoundsModal, setShowRoundsModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IMetricsDataModel>();
  const { resetGridState, onStateUpdated, checkQuickFilter } = usePfGridState(view.id);
  // Read once without subscribing: the search the previous grid for this view was showing.
  const store = useStore();
  const [carriedQuickFilter] = useState(() => quickFilterTextFor(store.get(quickFilterAtom), view.id));

  // Belt-and-suspenders sanitisation right before ag-grid sees the state
  // (deserializeView already sanitizes, but later mutations would slip
  // through), plus the partialColumnState stamp — see initialGridStateFor.
  const initialGridState = useMemo(
    () =>
      initialGridStateFor(view, columnsMeta, (slice) =>
        console.warn(`gridStateSanitizer:corruption-detected slice=${slice} (mount-time)`)
      ),
    [view, columnsMeta]
  );

  const treeColumnsDef = useMemo(
    () => (treeData ? treeColumnDefs(columnsDef) : columnsDef),
    [columnsDef, treeData]
  );

  const gridInitialState = useMemo(
    () => (treeData ? treeInitialState(initialGridState) : initialGridState),
    [initialGridState, treeData]
  );

  const onShowRoundsModal = useCallback((event: ICompanyViewGridEvent) => {
    setShowRoundsModal(true);
    setSelectedRowData(event.data);
  }, []);

  const onCloseRoundsModal = useCallback(() => {
    setShowRoundsModal(false);
  }, []);

  const isAuditView = PermissionService.get().isAuditor();

  const getMainMenuItems = useMemo(() => {
    const auditAware = createAuditViewMenuItems(isAuditView);
    return treeData ? withoutAggregationMenu(auditAware) : auditAware;
  }, [isAuditView, treeData]);

  // Tree leaves are per (company, vehicle), so a company held through two vehicles would
  // double-count in every KPI; reduce to one flat metric row per company first.
  const collectPublishedRows = useCallback(
    (api: GridApi): unknown[] => {
      const visible: unknown[] = [];
      api.forEachNodeAfterFilterAndSort((n) => {
        if (!n.group) {
          visible.push(n.data);
        }
      });
      if (!treeData) {
        return visible;
      }
      // Both publish sites funnel through here, so the ancestors' re-pool upstream can never
      // disagree with the KPI publish about which leaves survived the filter.
      onVisibleTreeLeavesChange?.(visibleTreeLeafKeys(visible as IPortfolioTreeRow[]));
      return publishedRowsFromTree(
        visible as IPortfolioTreeRow[],
        treeMetricsByCompanyId ?? EMPTY_METRICS_BY_COMPANY_ID
      );
    },
    [treeData, treeMetricsByCompanyId, onVisibleTreeLeavesChange]
  );

  const setFilteredData = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    const filteredData = collectPublishedRows(gridApiRef.current);
    // One transition, so the grand total and the KPI bar can never paint disagreeing numbers.
    startTransition(() => {
      setCompanyIdsCount(distinctCompanyCount(filteredData));
      setPublishedAsOfDate(basisTracker.ingestedAsOfDate());
      if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER) {
        setFilteredRoundTrackerMetrics(filteredData as IInvestmentDataModel[]);
      } else {
        setFilteredMetricsPublish(basisTracker.pairWithIngestedBasis(filteredData as IMetricsDataModel[]));
      }
    });
  }, [
    basisTracker,
    collectPublishedRows,
    setFilteredMetricsPublish,
    setFilteredRoundTrackerMetrics,
    setPublishedAsOfDate,
    view.companyViewType,
  ]);

  // Force-refresh the grand-total row so its "Total (N)" count reflects the latest
  // companyIdsCount. Pre-v35 recreating defaultAutoGroupColumnDef refreshed this
  // implicitly; v35 doesn't, so the grouped total showed a stale 0 until scroll.
  useEffect(() => {
    if (!gridApiRef.current) {
      return;
    }
    refreshGrandTotalCount(gridApiRef.current);
  }, [companyIdsCount]);

  useEffect(() => {
    if (!gridApiRef.current) {
      return;
    }
    gridApiRef.current?.onFilterChanged();
  }, [filteredFunds]);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode) => {
      // ROUND_TRACKER has no fund-filter concept; isExternalFilterPresent() returns false
      // for it, so AG Grid never calls this in practice — the early return is defensive and
      // intentionally passes everything (incl. AG Grid's null-data group/aggregate rows).
      if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER) {
        return true;
      }
      // Funds are scoped upstream; re-filtering here would hide leaves their parent's number counts.
      if (treeData) {
        return true;
      }
      return companyPassesFundFilter(node?.data as IMetricsDataModel | undefined, filteredFunds);
    },
    [filteredFunds, treeData, view.companyViewType]
  );

  const isExternalFilterPresent = useCallback(() => {
    if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER) {
      return false;
    }
    return !!filteredFunds;
  }, [filteredFunds, view.companyViewType]);

  const processCellCallback = useCallback(
    (params: ProcessCellForExportParams) => {
      if (CustomCompanyColumns.has(params.column.getId())) {
        return getCompany(params?.node?.data?.companyId ?? -1)?.name ?? '';
      }
      if (params.column.getColDef().field === 'company.website') {
        return getCompany(params.node?.data?.companyId ?? -1)?.website ?? '';
      }
      return commonProcessCb(params);
    },
    [getCompany]
  );

  const handleBtnExport = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    const groupKeys = gridApiRef.current.getRowGroupColumns()?.map((c) => c.getColId());
    // the only reason we still need this is because we want to exclude certain columns from export;
    // ag-grid exports all visible columns in current order by default
    const orderedVisibleColumnKeys = gridApiRef.current
      ?.getColumnState()
      ?.filter((c) => !c.hide && !ExcludeFromExport.has(c.colId))
      .map((c) => c.colId);

    gridApiRef.current?.exportDataAsExcel({
      columnKeys: [...groupKeys, ...(orderedVisibleColumnKeys ?? [])],
      // Tree parent rows carry their own values; skipping them would export only the leaves.
      skipRowGroups: !treeData,
    });
  }, [treeData]);

  const setMenuTabs = useCallback((event: ColumnRowGroupChangedEvent) => {
    if (!gridApiRef.current) {
      return;
    }
    const isGrouping = event.api.getRowGroupColumns().length > 0;
    const allColDefs = event.api.getColumnDefs();

    if (!allColDefs || !allColDefs.length) {
      return;
    }

    const newDefs: ColDef[] = [];
    if (isGrouping) {
      allColDefs.forEach((colDef: ColDef) => {
        const hasGeneralMenu = colDef.allowedAggFuncs?.length || colDef.colId === AUTO_GROUP_COL_ID;
        newDefs.push(
          hasGeneralMenu
            ? {
                ...colDef,
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
              }
            : colDef
        );
      });
    } else {
      allColDefs.forEach((colDef) => {
        newDefs.push({
          ...colDef,
          menuTabs: ['filterMenuTab'],
        });
      });
    }

    gridApiRef.current.setGridOption('columnDefs', newDefs);
  }, []);

  const onGridPreDestroyed = useCallback(() => {
    // eslint-disable-next-line
    // @ts-expect-error
    gridApiRef.current?.removeEventListener?.(CompanyViewGridEventType.showRoundDetails, onShowRoundsModal);
    resetGridState();
  }, [onShowRoundsModal, resetGridState]);

  const defaultAutoGroupColumnDef: ColDef = useMemo(
    () => ({
      width: 200,
      cellRendererParams: {
        totalValueGetter: () => {
          return `Total (${companyIdsCount})`;
        },
      },
      menuTabs: ['generalMenuTab'],
      filter: 'agGroupColumnFilter',
    }),
    [companyIdsCount]
  );

  const onColumnRowGroupChanged = useCallback(
    (event: ColumnRowGroupChangedEvent) => {
      if (treeData) {
        return;
      }
      const rowGroups = getRowGroupColumnState(event.api.getColumnState());
      setRowGroupColumns?.(rowGroups);
      setMenuTabs(event);
    },
    [setRowGroupColumns, setMenuTabs, treeData]
  );

  const onTreeAwareStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      if (!treeData) {
        onStateUpdated(event);
        return;
      }
      onStateUpdated({ ...event, state: restoreGroupingIntoState(event.state, initialGridState) });
    },
    [initialGridState, onStateUpdated, treeData]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApiRef.current = params.api;
      params.api.addEventListener(
        // eslint-disable-next-line
        // @ts-expect-error
        CompanyViewGridEventType.showRoundDetails,
        onShowRoundsModal
      );

      // `quickFilterText` is a live grid option, not saved grid state, so a remount would drop it.
      if (carriedQuickFilter) {
        params.api.setGridOption('quickFilterText', carriedQuickFilter);
      }

      setGridApi(params.api);
    },
    [carriedQuickFilter, onShowRoundsModal, setGridApi]
  );

  const gridOptions: GridOptions = useMemo(() => ({ onGridReady, ...pfBaseGridOptions }), [onGridReady]);

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      if (gridApiRef.current) {
        startTransition(() => {
          checkQuickFilter(event);
          setFilteredData();
        });
      }
    },
    [setFilteredData, checkQuickFilter]
  );

  // FD-2691: Provide a stable id per row so AG Grid can diff rowData updates and reuse
  // existing row DOM/cell components for unchanged rows. Without this, every rowData prop
  // change (e.g. after a fund-filter refetch) tears down and rebuilds every row, causing
  // company-logo cells to remount and briefly flash a fallback avatar.
  // Scoped to ROUND_TRACKER only — company views can have multiple rows per companyId
  // when split by fund, so a companyId-based id wouldn't be unique. AG Grid's default
  // sequential ids are fine for those views.
  const isRoundTracker = view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER;
  const getRowId = useCallback((params: GetRowIdParams) => {
    const d = params.data as IInvestmentDataModel;
    return `${d.companyId}-${d.investmentRoundId}-${d.transactionType ?? ''}-${d.sequence ?? ''}`;
  }, []);

  // Rows can be ingested with no filter change at all, and `firstDataRendered` is one-shot, so the
  // KPI atoms and the count are republished here against the basis the rows were built under.
  const onRowDataUpdated = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }
    // the rows the grid just ingested were built under this basis and for this date
    basisTracker.onRowDataIngested(fundBasis, asOfDate);
    const filteredData = collectPublishedRows(gridApiRef.current);
    startTransition(() => {
      setCompanyIdsCount(distinctCompanyCount(filteredData));
      setPublishedAsOfDate(basisTracker.ingestedAsOfDate());
      if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER) {
        setFilteredRoundTrackerMetrics(filteredData as IInvestmentDataModel[]);
      } else {
        setFilteredMetricsPublish(basisTracker.pairWithIngestedBasis(filteredData as IMetricsDataModel[]));
      }
    });
  }, [
    basisTracker,
    collectPublishedRows,
    view.companyViewType,
    setFilteredRoundTrackerMetrics,
    setFilteredMetricsPublish,
    setPublishedAsOfDate,
    fundBasis,
    asOfDate,
  ]);

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      if (!view) {
        return;
      }

      // The tree grid carries no row-group columns; reading them back would clear the saved grouping.
      if (!treeData) {
        const rowGroups = getRowGroupColumnState(event.api.getColumnState());
        setRowGroupColumns?.(rowGroups);
      }

      setFilteredData();
      setIsFirstRenderDone(true);
    },
    [setFilteredData, setIsFirstRenderDone, setRowGroupColumns, treeData, view]
  );

  // initialState.rowGroupExpansion is dropped on async data; restore per-node instead.
  const isGroupOpenByDefault = useCallback(
    (params: IsGroupOpenByDefaultParams) => isRowGroupExpandedByDefault(view, params.rowNode),
    [view]
  );

  const frameworkComponents = useMemo(
    () => ({
      fundComponentFilter: FundFilterComponent,
    }),
    []
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      menuTabs: ['filterMenuTab'],
      comparator: genericComparator,
      flex: 1,
      minWidth: 140,
      keyCreator: genericKeyCreator,
      getQuickFilterText: () => '',
    }),
    []
  );

  const fileName = usePortfolioGridFileName(view);

  const exportParams = useMemo(() => {
    return {
      processCellCallback,
      fileName,
    };
  }, [fileName, processCellCallback]);

  const aggFuncs = useMemo(() => {
    return { ...customAggFuncs };
  }, []);

  const quickFilterPrefs = useMemo(() => {
    return getQuickSearchColumnsFromPrefs();
  }, []);
  const roundsById = useAtomValue(roundsByIdMapAtom);
  const aggFuncConfig = useAtomValue(aggFuncConfigState(view.companyViewType));
  const context: SharedTableContext = useMemo(() => {
    return {
      asOfDate: viewDate,
      totalCompanies: companyIdsCount,
      roundsById,
      quickFilterPrefs,
      aggFuncs: aggFuncConfig,
    };
  }, [companyIdsCount, quickFilterPrefs, roundsById, viewDate, aggFuncConfig]);

  const onQuickFilterChange = useCallback((query: string) => {
    gridApiRef.current?.setGridOption('quickFilterText', query);
  }, []);

  const quickFilterPlaceholder = useMemo(() => {
    switch (view.companyViewType) {
      case COMPANY_VIEW_TYPE.PORTFOLIO_OVERVIEW:
      case COMPANY_VIEW_TYPE.RETURN_FORECAST:
        return getQuickSearchPlaceholderText();
      case COMPANY_VIEW_TYPE.ROUND_TRACKER:
        return 'Search by company';
      default:
        return undefined;
    }
  }, [view.companyViewType]);

  return (
    <section css={GridPanelContainer} key={`${view.id}-${viewDate}`}>
      <GenericFallbacksWrapper style={{ margin: '1rem 3rem' }}>
        <div css={PfGridHeadingSection}>
          <Stack direction={'row'} gap='0.5rem' alignItems={'center'} mb='0.5rem'>
            <SubSectionTitle>Companies Overview</SubSectionTitle>
          </Stack>

          <Stack
            display={'grid'}
            gridTemplateColumns={'1fr auto'}
            justifyContent={'space-between'}
            alignItems={'center'}
            mb='0.5rem'
          >
            <SearchInput
              onChange={onQuickFilterChange}
              defaultValue={carriedQuickFilter}
              style={{ maxWidth: '20rem' }}
              placeholder={quickFilterPlaceholder}
            />
            <Stack direction='row' alignItems={'center'}>
              {headingControls && <Box mr='0.75rem'>{headingControls}</Box>}
              <ColumnSelectorProvider>
                <ColumnsSettingsDropdown />
              </ColumnSelectorProvider>
              <ExportGridButton onClick={handleBtnExport} />
            </Stack>
          </Stack>
        </div>

        <div css={PfGridContainer}>
          {/* FD-2977: defer the grid mount until columnsDef is populated.
              ColumnsDefinitionProvider initializes columnsDef as [] and fills
              it from an async-loaded ColumnMeta atom in a post-mount effect.
              If we let AgTable mount with [], ag-grid v35 resolves
              initialState slices (rowGroup, columnVisibility, columnOrder, …)
              against an empty column list — colIds match nothing, every
              slice is silently dropped, the saved view ends up corrupted on
              the first onStateUpdated event after init. Gating the mount on
              columnsDef.length > 0 closes the race at its source: when ag-
              grid finally sees initialState, every colId resolves and the
              state lands on the first try. FD-4123: the `key` keys on view+date
              only, so later columnsDef updates hit the live grid rather than
              remounting. `maintainColumnOrder` is what protects the order there. */}
          {treeColumnsDef.length > 0 && (
            <AgTable
              key={`${view.id}-${viewDate}`}
              initialState={gridInitialState}
              loading={loading}
              rowData={rowData}
              columnDefs={treeColumnsDef}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              getRowId={isRoundTracker ? getRowId : undefined}
              onFilterChanged={onFilterChanged}
              onRowDataUpdated={onRowDataUpdated}
              onFirstDataRendered={onFirstDataRendered}
              onColumnRowGroupChanged={onColumnRowGroupChanged}
              treeData={treeData}
              getDataPath={treeData ? (getDataPath as (data: unknown) => string[]) : undefined}
              groupDefaultExpanded={treeData ? 1 : undefined}
              // ag-grid consults the callback first and only falls back to groupDefaultExpanded
              // when it is absent; the saved expansion ids are row-group node ids.
              isGroupOpenByDefault={treeData ? undefined : isGroupOpenByDefault}
              onGridPreDestroyed={onGridPreDestroyed}
              onStateUpdated={onTreeAwareStateUpdated}
              components={frameworkComponents}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesExternalFilterPass}
              autoGroupColumnDef={
                treeData && treeAutoGroupColumnDef ? treeAutoGroupColumnDef : defaultAutoGroupColumnDef
              }
              defaultExcelExportParams={exportParams}
              defaultCsvExportParams={exportParams}
              suppressAggFuncInHeader
              aggFuncs={aggFuncs}
              context={context}
              suppressScrollOnNewData
              grandTotalRow={treeData ? undefined : 'top'}
              getMainMenuItems={getMainMenuItems}
            />
          )}
        </div>
      </GenericFallbacksWrapper>
      <GenericFallbacksWrapper>
        <Dialog maxWidth={false} open={showRoundsModal} onClose={onCloseRoundsModal}>
          {showRoundsModal ? (
            <Box>
              <RoundsModalContents
                mainGridColDef={clone(gridApiRef.current?.getColumnDefs()) ?? []}
                mainRowData={clone(selectedRowData!)}
              />
            </Box>
          ) : (
            <></>
          )}
        </Dialog>
      </GenericFallbacksWrapper>
    </section>
  );
}

export function SharedTable(props: ISharedTableProps) {
  return (
    <ColumnsDefinitionProvider view={props.view}>
      <SharedTableImpl {...props} />
    </ColumnsDefinitionProvider>
  );
}

function getRowGroupColumnState(columnsState: ColumnState[]) {
  return columnsState.filter((c) => c.rowGroup);
}

/**
 * Tracks which fund basis the grid's ingested rows were built under, so every publish pairs rows
 * with THEIR basis. A fund change re-runs the filter before the new rows land, so pairing with the
 * current selection would stamp old rows with a new basis — the FD-4341 mixed-basis flash. The
 * basis therefore only advances on row ingestion, never on a selection change alone.
 */
export function createPublishedBasisTracker(
  initialBasis: number[] | null,
  initialAsOfDate: string | null = null
) {
  let ingestedBasis = initialBasis;
  let ingestedAsOfDate = initialAsOfDate;
  return {
    onRowDataIngested(basis: number[] | null, asOfDate: string | null = null) {
      ingestedBasis = basis;
      ingestedAsOfDate = asOfDate;
    },
    pairWithIngestedBasis(metrics: IMetricsDataModel[]): IFilteredMetricsPublish {
      return { metrics, fundIds: ingestedBasis };
    },
    /** Plain date the ingested rows were built for; null when the container did not say. */
    ingestedAsOfDate() {
      return ingestedAsOfDate;
    },
  };
}

// When grouping, the "Total (N)" count lives in the auto group column; otherwise in
// the CompanyNameAndLogo footer cell. Force-refresh whichever holds it.
export function refreshGrandTotalCount(api: GridApi) {
  const isGrouping = api.getRowGroupColumns()?.length > 0;
  api.refreshCells({
    force: true,
    columns: [isGrouping ? AUTO_GROUP_COL_ID : CompanyNameAndLogo.NAME],
  });
}

export function FundSelectorButton({ children, ...rest }: PropsWithChildren) {
  return (
    <Button endIcon={<FilterListOutlinedIcon />} size='large' title='Filter by fund' {...rest}>
      {children}
    </Button>
  );
}
